import React, { useState, useEffect, useRef } from "react";
import { FaWhatsapp, FaTwitter, FaLinkedin, FaLink } from "react-icons/fa";

export default function SharePopup({ isOpen, link, onClose = () => {} }) {
  const [copied, setCopied] = useState(false);
  const closeTimerRef = useRef(null);

  // Reset "Copied" label after 2s (moved above early return so hooks run unconditionally)
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  // Clear any pending close timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  // prevent background scroll when popup is open
  const prevBodyStyleRef = useRef({
    overflow: "",
    position: "",
    width: "",
    paddingRight: "",
  });

  useEffect(() => {
    if (isOpen) {
      const body = document.body;
      prevBodyStyleRef.current = {
        overflow: body.style.overflow || "",
        position: body.style.position || "",
        width: body.style.width || "",
        paddingRight: body.style.paddingRight || "",
      };
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.width = "100%";
    } else {
      const prev = prevBodyStyleRef.current;
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.width = prev.width;
      document.body.style.paddingRight = prev.paddingRight;
    }
    return () => {
      const prev = prevBodyStyleRef.current;
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.width = prev.width;
      document.body.style.paddingRight = prev.paddingRight;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const defaultMessage =
    "🚀 Learn smarter, retain more with 1xSkills – Upload PDFs, papers & Youtube videos → Get instant summaries, quizzes & chat models like GPT-5, Gemini, Claude Opus 4. \n  All in your personalized AI knowledge Workspace 👉";
  const displayedMessage = "Let your friends know how awesome 1xSkills is!";
  const resolvedLink = link || (typeof window !== "undefined" ? window.location.origin : "");

  const encodedMessage = encodeURIComponent(defaultMessage);
  const encodedLink = encodeURIComponent(resolvedLink);

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: <FaWhatsapp className="text-green-500 text-xl" />,
      url: `https://api.whatsapp.com/send?text=${encodedMessage}%20${encodedLink}`,
    },
    {
      name: "Twitter",
      icon: <FaTwitter className="text-sky-500 text-xl" />,
      url: `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedLink}`,
    },
    {
      name: "LinkedIn",
      icon: <FaLinkedin className="text-blue-700 text-xl" />,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedLink}`,
    },
  ];

  // Copy to clipboard handler (copies message + link; no alerts; toggle "Copied" text then close)
  const copyToClipboard = async () => {
    const textToCopy = `${defaultMessage}\n\n${resolvedLink}`; // include message + link
    if (!textToCopy || !textToCopy.trim()) {
      return;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = textToCopy;
        textarea.style.position = "fixed"; // avoid scrolling to bottom
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      // show "Copied" briefly, then close
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        onClose();
      }, 800);
    } catch (err) {
      setCopied(false);
    }
  };

  return (
    // Close when clicking outside (overlay)
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      onClick={() => onClose()}
    >
      {/* Prevent overlay click from closing when interacting with panel */}
      <div
        className="bg-white p-6 rounded-2xl shadow-lg w-80 text-center relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Message */}
        <h2 className="text-lg font-semibold mb-3">Share a word!</h2>
        <p className="text-sm text-gray-600 mb-4">{displayedMessage}</p>

        {/* Share buttons with icons */}
        <div className="grid grid-cols-2 gap-3">
          {shareOptions.map((option) => (
            <a
              key={option.name}
              href={option.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onClose()} // close popup when a share option is clicked
              className="flex items-center justify-center gap-2 py-2 rounded-lg border hover:bg-gray-100 transition"
            >
              {option.icon}
              <span className="text-sm font-medium">{option.name}</span>
            </a>
          ))}

          {/* Copy Link button */}
          <button
            type="button"
            onClick={copyToClipboard}
            className="flex items-center justify-center gap-2 py-2 rounded-lg border hover:bg-gray-100 transition"
          >
            <FaLink className="text-gray-600 text-xl" />
            <span className="text-sm font-medium">
              {copied ? "Copied" : "Copy Link"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
